import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import company from '../data/company-about.json';
import journalPosts from '../data/journal-posts.json';
import journalArchives from '../data/journal-archives.json';
import discovery from './publicDiscovery.js';
import {
  absoluteShareUrl,
  buildSocialMetaTags,
  PAGE_META,
  SITE_ORIGIN,
} from './shareMeta';

function normalizeConfig(titleOrConfig, description, options = {}) {
  if (typeof titleOrConfig === 'object' && titleOrConfig !== null) {
    return titleOrConfig;
  }
  return {
    title: titleOrConfig,
    description,
    ...options,
  };
}

function upsertMeta(attr, key, content) {
  if (!content) return null;

  const selector =
    attr === 'property'
      ? `meta[property="${key}"]`
      : `meta[name="${key}"]`;

  let el = document.querySelector(selector);
  const previous = el?.getAttribute('content') ?? null;

  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
  return { el, previous, attr, key };
}

function upsertCanonical(path) {
  const href = absoluteShareUrl(path);
  let el = document.querySelector('link[rel="canonical"]');
  const previous = el?.getAttribute('href') ?? null;

  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
  return { el, previous };
}

/**
 * Sets document title, description, Open Graph, Twitter, and canonical URL.
 * Pass a PAGE_META entry object or (title, description, { image, path }).
 */
export function usePageMeta(titleOrConfig, description, options = {}) {
  const location = useLocation();
  const config = normalizeConfig(titleOrConfig, description, options);
  const {
    title,
    description: metaDescription,
    image,
    path = location.pathname,
  } = config;

  useEffect(() => {
    const previousTitle = document.title;
    document.title = title;

    const tags = buildSocialMetaTags({
      title,
      description: metaDescription,
      image,
      path,
    });

    const metaSnapshots = tags.map(({ attr, key, content }) =>
      upsertMeta(attr, key, content)
    );

    const canonicalSnapshot = upsertCanonical(path);
    const existingSchema = document.getElementById('grupa-structured-data');
    const previousSchema = existingSchema?.textContent ?? null;
    const article = [...journalPosts, ...journalArchives].find(post => '/blog/' + post.slug === discovery.canonicalPath(path));
    const schema = discovery.structuredData({ title, description: metaDescription, path, image }, company, article);
    let schemaElement = existingSchema;
    if (schema) {
      if (!schemaElement) {
        schemaElement = document.createElement('script');
        schemaElement.id = 'grupa-structured-data';
        schemaElement.type = 'application/ld+json';
        document.head.appendChild(schemaElement);
      }
      schemaElement.textContent = discovery.serializeJson(schema);
    } else if (schemaElement) {
      schemaElement.remove();
    }

    return () => {
      document.title = previousTitle;
      if (previousSchema === null) {
        schemaElement?.remove();
      } else {
        existingSchema.textContent = previousSchema;
        if (!existingSchema.isConnected) document.head.appendChild(existingSchema);
      }
      metaSnapshots.forEach((snapshot) => {
        if (!snapshot?.el) return;
        if (snapshot.previous === null) {
          snapshot.el.remove();
        } else {
          snapshot.el.setAttribute('content', snapshot.previous);
        }
      });
      if (canonicalSnapshot.el) {
        if (canonicalSnapshot.previous === null) {
          canonicalSnapshot.el.remove();
        } else {
          canonicalSnapshot.el.setAttribute('href', canonicalSnapshot.previous);
        }
      }
    };
  }, [title, metaDescription, image, path]);
}

export { PAGE_META, SITE_ORIGIN };
